// MessagesDropdown.jsx
// Real-data messages icon + dropdown for the Navbar.
// Pulls from `conversations` + `chat_messages` tables, subscribes to realtime inserts.
// Shows numeric badge when there are unread messages; dropdown previews recent threads.
// Retokened onto the same T object as CustomerSettingsPage.jsx so it renders
// identically whichever shell (pro dashboard or customer settings) it's dropped into.

import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import { useBreakpoint } from "../hooks/useBreakpoint";

/* ─── Design tokens — identical set to CustomerSettingsPage.jsx (T) ─── */
const T = {
  green:       "#1e9254",
  greenDark:   "#155c36",
  greenTint:   "#eef8f3",
  greenBd:     "#a7d9bc",

  white:       "#ffffff",
  card2:       "#f7f8fa",
  border:      "#e4e7ef",
  borderFaint: "#eef0f4",
  hoverBg:     "#F3F4F6",

  text:        "#0f1117",
  textMid:     "#2d3549",
  sub:         "#4a5268",
  faint:       "#8b93a7",
  ghost:       "#c7cddb",

  amber:       "#92400e",
  amberTint:   "#fefaf1",
  amberBd:     "#f3d99a",

  red:         "#b3261e",
  redTint:     "#fdf3f2",
  redBd:       "#f0c9c5",

  blue:        "#1d4ed8",
  blueTint:    "#eef3fd",
  blueBd:      "#c7d7f7",

  gold:        "#b8862f",
  goldTint:    "#fbf5e9",
  goldBd:      "#e9d5a3",

  sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",

  rFull: "9999px",
  tx: "all 0.18s cubic-bezier(.4,0,.2,1)",
};

/* ─── Helpers ─── */
function timeAgo(ts) {
  if (!ts) return "";
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  const hrs  = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1)   return "now";
  if (mins < 60)  return `${mins}m`;
  if (hrs < 24)   return `${hrs}h`;
  if (days === 1) return "yesterday";
  return new Date(ts).toLocaleDateString("en-KE", { month: "short", day: "numeric" });
}

function getInitials(name) {
  if (!name) return "?";
  return name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
}

/* ─── Avatar colour pool — same tint/fg pairing as <Badge> on the
   settings page (green / blue / amber / gold / red), deterministic by name. ─── */
const AVATAR_PAIRS = [
  { bg: T.greenTint, fg: T.greenDark },
  { bg: T.blueTint,  fg: T.blue },
  { bg: T.amberTint, fg: T.amber },
  { bg: T.goldTint,  fg: T.gold },
  { bg: T.redTint,   fg: T.red },
  { bg: T.card2,     fg: T.sub },
];
function avatarColor(name) {
  const idx = (name || "").charCodeAt(0) % AVATAR_PAIRS.length;
  return AVATAR_PAIRS[idx] || AVATAR_PAIRS[0];
}

/* ══════════════════════════════════════════════
   COMPONENT
══════════════════════════════════════════════ */
export default function MessagesDropdown() {
  const navigate          = useNavigate();
  const { user, isLoggedIn } = useAuth();
  const { isMobile }      = useBreakpoint();

  const [open, setOpen]           = useState(false);
  const [threads, setThreads]     = useState([]);   // [{id, otherName, lastMsg, lastTs, unread}]
  const [totalUnread, setTotalUnread] = useState(0);
  const [loading, setLoading]     = useState(false);

  const wrapRef    = useRef(null);
  const channelRef = useRef(null);

  /* ── Fetch conversations ── */
  const fetchThreads = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      // Pull the user's conversations, ordered by most-recent message
      const { data: convos, error } = await supabase
        .from("conversations")
        .select(`
          id,
          customer_id,
          pro_id,
          updated_at,
          chat_messages (
            id,
            content,
            sender_id,
            created_at,
            read
          )
        `)
        .or(`customer_id.eq.${user.id},pro_id.eq.${user.id}`)
        .order("updated_at", { ascending: false })
        .limit(20);

      if (error) throw error;

      // For each conversation, resolve the other party's display name from profiles
      const otherIds = (convos || []).map(c =>
        c.customer_id === user.id ? c.pro_id : c.customer_id
      ).filter(Boolean);

      const uniqueIds = [...new Set(otherIds)];
      let profileMap = {};
      if (uniqueIds.length) {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, full_name, avatar_url")
          .in("id", uniqueIds);
        (profiles || []).forEach(p => { profileMap[p.id] = p; });
      }

      const mapped = (convos || []).map(c => {
        const otherId   = c.customer_id === user.id ? c.pro_id : c.customer_id;
        const otherProf = profileMap[otherId] || {};
        const msgs      = (c.chat_messages || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        const lastMsg   = msgs[0];
        const unreadCount = msgs.filter(m => !m.read && m.sender_id !== user.id).length;

        return {
          id:        c.id,
          otherId,
          otherName: otherProf.full_name || "JiraniPro",
          avatarUrl: otherProf.avatar_url || null,
          lastMsg:   lastMsg?.content || "",
          lastTs:    lastMsg?.created_at || c.updated_at,
          unread:    unreadCount,
        };
      });

      setThreads(mapped.slice(0, 6));
      setTotalUnread(mapped.reduce((acc, t) => acc + t.unread, 0));
    } catch (err) {
      console.error("MessagesDropdown fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!isLoggedIn) return;
    fetchThreads();
  }, [isLoggedIn, fetchThreads]);

  /* ── Realtime: new message → refetch ── */
  useEffect(() => {
    if (!user?.id) return;
    channelRef.current = supabase
      .channel(`messages-badge-${user.id}`)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "chat_messages",
      }, () => {
        fetchThreads();
      })
      .on("postgres_changes", {
        event: "UPDATE",
        schema: "public",
        table: "chat_messages",
      }, () => {
        fetchThreads();
      })
      .subscribe();

    return () => {
      if (channelRef.current) supabase.removeChannel(channelRef.current);
    };
  }, [user?.id, fetchThreads]);

  /* ── Close on outside click ── */
  useEffect(() => {
    const handler = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* ── Close on Escape ── */
  useEffect(() => {
    const handler = e => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  /* ── Mark a thread's messages as read ── */
  const markThreadRead = async (conversationId) => {
    setThreads(prev =>
      prev.map(t => t.id === conversationId ? { ...t, unread: 0 } : t)
    );
    setTotalUnread(prev => {
      const thread = threads.find(t => t.id === conversationId);
      return Math.max(0, prev - (thread?.unread || 0));
    });
    try {
      await supabase
        .from("chat_messages")
        .update({ read: true })
        .eq("conversation_id", conversationId)
        .neq("sender_id", user.id);
    } catch {}
  };

  const handleThreadClick = (thread) => {
    markThreadRead(thread.id);
    setOpen(false);
    navigate(`/messages/${thread.id}`);
  };

  const cappedBadge = Math.min(totalUnread, 99);

  if (!isLoggedIn) return null;

  return (
    <div ref={wrapRef} style={{ position: "relative", display: "inline-flex" }}>
      {/* ── Trigger button ── */}
      <button
        onClick={() => setOpen(o => !o)}
        title="Messages"
        style={{
          width: isMobile ? 27 : 34, height: isMobile ? 27 : 34, borderRadius: "50%",
          background: T.blueTint, border: `1px solid ${T.blueBd}`,
          cursor: "pointer", display: "flex", alignItems: "center",
          justifyContent: "center", position: "relative", padding: 0,
          transition: "transform 0.15s ease, box-shadow 0.15s ease",
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(29,78,216,0.20)"; }}
        onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
      >
        {/* Message icon */}
        <svg width={isMobile ? 12 : 14} height={isMobile ? 12 : 14} viewBox="0 0 24 24" fill="none"
          stroke={T.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>

        {/* Numeric badge */}
        {cappedBadge > 0 && (
          <span style={{
            position: "absolute",
            top: isMobile ? -2 : -3,
            right: isMobile ? -2 : -3,
            minWidth: isMobile ? 13 : 15, height: isMobile ? 13 : 15,
            borderRadius: T.rFull,
            background: T.red,
            border: "1.5px solid #fff",
            color: "#fff",
            fontSize: isMobile ? 8 : 9,
            fontWeight: 700,
            fontFamily: T.sans,
            display: "flex", alignItems: "center", justifyContent: "center",
            lineHeight: 1,
            pointerEvents: "none",
          }}>
            {cappedBadge > 9 ? "9+" : cappedBadge}
          </span>
        )}
      </button>

      {/* ── Dropdown ── */}
      {open && (
        <div style={{
          position: "absolute",
          top: "calc(100% + 10px)",
          right: 0,
          width: 340,
          background: T.white,
          border: `1.5px solid ${T.border}`,
          borderRadius: 14,
          boxShadow: "0 8px 32px rgba(13,46,26,0.14), 0 2px 8px rgba(13,46,26,0.05)",
          zIndex: 9999,
          overflow: "hidden",
          animation: "msgDropIn 0.17s cubic-bezier(.4,0,.2,1) both",
        }}>
          <style>{`
            @keyframes msgDropIn {
              from { opacity: 0; transform: translateY(-6px); }
              to   { opacity: 1; transform: translateY(0); }
            }
          `}</style>

          {/* Header */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "14px 16px 10px",
            borderBottom: `1px solid ${T.borderFaint}`,
          }}>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: T.text, fontFamily: T.sans }}>
              Messages {totalUnread > 0 && (
                <span style={{ color: T.green, fontWeight: 700 }}>({totalUnread})</span>
              )}
            </span>
            <button
              onClick={() => { setOpen(false); navigate("/messages"); }}
              style={{
                background: "none", border: "none", fontSize: 12, fontWeight: 600,
                color: T.green, cursor: "pointer", fontFamily: T.sans, padding: 0,
              }}
            >
              See all
            </button>
          </div>

          {/* Thread list */}
          <div style={{ maxHeight: 360, overflowY: "auto" }} className="cs-scroll">
            {loading && threads.length === 0 ? (
              /* Loading skeleton */
              [1, 2, 3].map(i => (
                <div key={i} style={{ display: "flex", gap: 12, padding: "12px 16px", alignItems: "center" }}>
                  <div style={{ width: 38, height: 38, borderRadius: "50%", background: T.card2, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ height: 11, width: "60%", background: T.card2, borderRadius: 4, marginBottom: 6 }} />
                    <div style={{ height: 10, width: "80%", background: T.card2, borderRadius: 4 }} />
                  </div>
                </div>
              ))
            ) : threads.length === 0 ? (
              <div style={{
                padding: "36px 20px",
                textAlign: "center",
                color: T.faint,
                fontFamily: T.sans,
                fontSize: 13,
              }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={T.ghost}
                  strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"
                  style={{ margin: "0 auto 10px", display: "block" }}>
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                <p style={{ margin: 0, fontWeight: 600, color: T.sub, fontSize: 14, fontFamily: T.sans }}>No messages yet</p>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: T.faint, lineHeight: 1.5, fontFamily: T.sans }}>
                  Messages from your pros will appear here.
                </p>
              </div>
            ) : (
              threads.map((thread, i) => {
                const { bg, fg } = avatarColor(thread.otherName);
                const isLast = i === threads.length - 1;
                return (
                  <div
                    key={thread.id}
                    onClick={() => handleThreadClick(thread)}
                    className="cs-dropdown-item"
                    style={{
                      display: "flex", gap: 12, padding: "12px 16px",
                      alignItems: "flex-start", cursor: "pointer",
                      background: thread.unread > 0 ? T.greenTint : T.white,
                      borderBottom: isLast ? "none" : `1px solid ${T.borderFaint}`,
                      transition: T.tx,
                      borderLeft: `3px solid ${thread.unread > 0 ? T.green : "transparent"}`,
                    }}
                  >
                    {/* Avatar */}
                    <div style={{
                      width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
                      background: bg, color: fg,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 12, fontWeight: 700, fontFamily: T.sans,
                      border: `1.5px solid ${T.white}`,
                      boxShadow: "0 1px 3px rgba(13,46,26,0.08)",
                      overflow: "hidden",
                      position: "relative",
                    }}>
                      {thread.avatarUrl
                        ? <img src={thread.avatarUrl} alt={thread.otherName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        : getInitials(thread.otherName)
                      }
                      {/* Unread dot on avatar */}
                      {thread.unread > 0 && (
                        <span style={{
                          position: "absolute", top: -1, right: -1,
                          width: 10, height: 10, borderRadius: "50%",
                          background: T.green, border: `2px solid ${T.white}`,
                        }} />
                      )}
                    </div>

                    {/* Text content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 3 }}>
                        <span style={{
                          fontSize: 13, fontWeight: thread.unread > 0 ? 700 : 500,
                          color: T.text, fontFamily: T.sans,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>
                          {thread.otherName}
                        </span>
                        <span style={{ fontSize: 11, color: T.faint, flexShrink: 0, fontFamily: T.sans }}>
                          {timeAgo(thread.lastTs)}
                        </span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
                        <p style={{
                          margin: 0, fontSize: 12, color: thread.unread > 0 ? T.textMid : T.sub,
                          fontFamily: T.sans, lineHeight: 1.4,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          fontWeight: thread.unread > 0 ? 500 : 400,
                          maxWidth: 220,
                        }}>
                          {thread.lastMsg || "No messages yet"}
                        </p>
                        {thread.unread > 0 && (
                          <span style={{
                            flexShrink: 0, minWidth: 18, height: 18,
                            borderRadius: T.rFull, background: T.green,
                            color: "#fff", fontSize: 10, fontWeight: 700,
                            fontFamily: T.sans,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            padding: "0 4px",
                          }}>
                            {thread.unread > 9 ? "9+" : thread.unread}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer CTA */}
          {threads.length > 0 && (
            <div style={{ padding: "12px 16px", borderTop: `1px solid ${T.borderFaint}` }}>
              <button
                onClick={() => { setOpen(false); navigate("/messages"); }}
                style={{
                  width: "100%", background: T.green, color: "#fff",
                  border: "none", borderRadius: 10, padding: "10px",
                  fontSize: 13, fontWeight: 600, cursor: "pointer",
                  fontFamily: T.sans, transition: T.tx,
                }}
                onMouseEnter={e => { e.currentTarget.style.background = T.greenDark; }}
                onMouseLeave={e => { e.currentTarget.style.background = T.green; }}
              >
                Open full inbox
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}